import moment from 'moment';
import { PeriodType } from '../typings';
import { ReactNode } from 'react';
import { Text } from '@chakra-ui/react';

export const durationData: PeriodType[] = [
  {
    id: 1,
    duration: '7D',
    startDate: moment().subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    sprintId: '',
  },
  {
    id: 2,
    duration: '14D',
    startDate: moment().subtract(14, 'day').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    sprintId: '',
  },
  {
    id: 3,
    duration: '21D',
    startDate: moment().subtract(21, 'day').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    sprintId: '',
  },
  {
    id: 4,
    duration: '1M',
    startDate: moment().subtract(30, 'day').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    sprintId: '',
  },
  {
    id: 5,
    duration: '3M',
    startDate: moment().subtract(90, 'day').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    sprintId: '',
  },
  {
    id: 6,
    duration: 'Custom',
    startDate: '',
    endDate: '',
    sprintId: '',
  },
  {
    id: 7,
    duration: 'Sprint',
    startDate: '',
    endDate: '',
    sprintId: '',
  },
  {
    id: 8,
    duration: 'Today',
    startDate: '',
    endDate: '',
    sprintId: '',
  },
  {
    id: 9,
    duration: 'Month',
    startDate: '',
    endDate: '',
    sprintId: '',
  },
  {
    id: 10,
    duration: 'Quarter',
    startDate: '',
    endDate: '',
    sprintId: '',
  },
];

export const eliminateHtmlTags = (tag: string) => {
  return tag.replace(/<\/?[^>]+(>|$)/g, '');
};

export const convertHexToRGBA = (hexCode: string, opacity: number) => {
  let hex = hexCode.replace('#', '');

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  /* Backward compatibility for whole number based opacity values. */
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }

  return `rgba(${r},${g},${b},${opacity})`;
};

export function isHTML(str: string) {
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
}

export function locFormat(loc: number): string {
  if (loc >= 1_000_000) {
    return `${(loc / 1_000_000).toFixed(loc % 1_000_000 === 0 ? 0 : 1)}M`;
  } else if (loc >= 100_000) {
    return `${Math.floor(loc / 1_000)}k`;
  } else if (loc >= 10_000) {
    return `${Math.floor(loc / 1_000)}k`;
  } else if (loc >= 1_000) {
    return `${(loc / 1_000).toFixed(1)}k`;
  } else {
    return loc.toString();
  }
}

export const splitTime = (numberOfHours: number) => {
  if (numberOfHours === 0) return '0';

  const Days = Math.floor(numberOfHours / 24);
  const Remainder = numberOfHours % 24;
  const Hours = Math.floor(Remainder);
  const MinutesDecimal = 60 * (Remainder - Hours);
  const Minutes = Math.floor(MinutesDecimal);
  const Seconds = Math.floor((MinutesDecimal - Minutes) * 60);

  const showSeconds = numberOfHours < 1 && numberOfHours * 60 < 1;

  return Days !== 0
    ? `${isNaN(Days) ? '0d' : Days !== 0 ? Days + 'd' : ''} ${
        isNaN(Hours) ? '0h' : Hours ? Hours + 'h' : ''
      } ${isNaN(Minutes) ? '0m' : Minutes !== 0 ? Minutes + 'm' : ''} ${
        showSeconds ? `${Seconds}s` : ''
      }`
    : `${isNaN(Hours) ? '0h' : Hours ? Hours + 'h' : ''} ${
        isNaN(Minutes) ? '0m' : Minutes !== 0 ? Minutes + 'm' : ''
      } ${showSeconds ? `${Seconds}s` : ''}`;
};

export enum NumberStatus {
  positive = 'Positive',
  negative = 'Negative',
  zero = 'Zero',
}

export const checkNumber = (number: number | null) => {
  if (number === null) {
    return NumberStatus.zero;
  } else {
    return number > 0
      ? NumberStatus.positive
      : number < 0
      ? NumberStatus.negative
      : NumberStatus.zero;
  }
};

export const eliminateSpecialCharacters = (input: string) => {
  return input.replace(/[^\w\s]/gi, '');
};

export const eliminateSpecialCharactersNotHyphen = (input: string) => {
  return input.replace(/[^\w\s-]/gi, '');
};

//Query-> PrReportDetail
export const PR_CHART_KEYS = [
  'PR_UPDATED',
  'PR_OPEN',
  'PR_MERGE',
  'PR_REVIEW',
  'PR_REVIEWED',
  'PR_CHANGES',
  'MERGE_WITHOUT_REVIEW',
  'MERGE_WITH_REVIEW',
  'COMMENTS_PER_PR',
  'PR_CYCLE_TIME',
  'PICKUP_CYCLE_TIME',
  'MERGE_CYCLE_TIME',
  'CODING_CYCLE_TIME',
  'CODE_REVIEW_TURNAROUND_TIME',
  'COMMIT_WITHOUT_REVIEW',
  'FIRST_TIME_REVIEW',
  'PR_COMMENT',
  'PR_CHANGES_REQUESTED',
  'PR_WITHOUT_ISSUE',
  'TOP_REVIEWER_DISTRIBUTION',
  'PR_CYCLE_TIME_BREAKDOWN',
  'PR_REVIEW_TIME',
  'APPROVED_WITHOUT_COMMENT',
  'UNLINKED_PRS_PERCENTAGE',
  'PR_REVIEWER_DISTRIBUTION',
  'PR_WITHOUT_CHANGES_REQUESTED',
  'PR_REVIEW_WITHOUT_FEEDBACK',
  'PR_AGEING_COUNT',
];

// Query-> ChangeFailureDetail
export const CFR_CHART_KEYS = [
  'CHANGE_FAILURE',
  'CHANGE_FAILURE_RATE',
  'MEAN_TIME_TO_RECOVERY',
  'MEAN_TIME_TO_RECOVERY_DISTRIBUTION',
  'CHANGE_HEALTH',
];

// Query-> DeploymentDetails
export const DEPLOY_CHART_KEYS = [
  'DEPLOYMENT_FREQUENCY',
  'LEAD_TIME_FOR_CHANGE',
  'DEPLOYMENT_FREQUENCY_DISTRIBUTION',
  'LEAD_TIME_BREAKDOWN',
  'DEPLOY_CYCLE_TIME',
  'DELIVERY_LEAD_TIME',
];

// Query-> ContributorDetails
export const CONTRIBUTOR_CHART_KEYS = ['CONTRIBUTOR_COUNT'];

// Query-> issueReport
export const ISSUE_CHART_KEYS = [
  'DEFECT_ESCAPE_RATE',
  'ISSUE_UPDATED',
  'TASK_CLOSE',
  'TASK_OPEN',
  'BUG_CLOSE',
  'BUG_OPEN',
  'PROD_BUG_OPEN',
  'STORY_POINT_COMPLETED',
  'STORY_POINT_ASSIGNED',
  'ISSUE_CYCLE_TIME',
  'BACKWARD_MOMENTUM',
  'ISSUE_THROUGHPUT',
  'BUG_THROUGHPUT',
  'ISSUE_CYCLE_TIME_VS_COUNT',
  'BUG_REOPENED',
  'ISSUE_REOPENED',
  'BUG_LEAD_TIME',
  'ISSUE_WITHOUT_STORY_POINT',
  'ISSUE_PROGRESSION_RATE',
  'TASK_IN_PROGRESS',
  'BUG_IN_PROGRESS',
  'OVERDUE_TICKETS',
  'BLOCKED_TICKETS',
  'TASK_WORKED_UPON',
  'BUG_WORKED_UPON',
  'BUG_REPORTED',
  'ISSUE_REPORTED',
  'ISSUE_BUG_DISTRIBUTION',
  'ALL_OVERDUE_TICKETS',
];

// Query-> GoalMetricDetailsGit
export const GOAL_METRIC_GIT_KEYS = [
  'PR_REVIEW_TIME',
  'PR_SIZE',
  'PR_WITHOUT_ISSUE',
  'COMMIT_WITHOUT_REVIEW',
  'FIRST_TIME_REVIEW',
];

// Query-> GoalMetricDetailsJira
export const GOAL_METRIC_JIRA_KEYS = [
  'ISSUE_WITHOUT_INVESTMENT',
  'ISSUE_WITHOUT_STORY_POINT',
  'ISSUE_CYCLE_TIME',
];

// Query-> WellBeingDetailsByType
export const WELLBEING_CHART_KEYS = [
  'BURN_OUT',
  'NON_WORKING_DAY',
  'OUT_OF_HOUR',
];

// Query-> TeamStatsWorkingAgreementDetails
export const WORKING_AGREEMENT_KEYS = ['WORKING_AGREEMENT_EXCEPTIONS'];

// Query-> CommitReportDetail
export const COMMIT_CHART_KEYS = [
  'COMMIT_COUNT',
  'NEW_LINE_OF_CODE',
  'DAYS_WITH_COMMITS',
];

// Query-> GetTeamMembers
export const MEMBER_CHART_KEYS = ['MEMBER_COUNT'];

// Query-> QualityBranchDetailByType
export const QUALITY_CHART_KEYS = [
  'QUALITY_CODE_SMELLS',
  'QUALITY_BUGS',
  'QUALITY_VULNERABILITIES',
  'QUALITY_SECURITY_HOTSPOTS',
  'QUALITY_COVERAGE',
  'QUALITY_DUPLICATED_LINES',

  'NEW_QUALITY_CODE_SMELLS',
  'NEW_QUALITY_BUGS',
  'NEW_QUALITY_VULNERABILITIES',
  'NEW_QUALITY_SECURITY_HOTSPOTS',
  'NEW_QUALITY_COVERAGE',
  'NEW_QUALITY_DUPLICATED_LINES',
];

export const DEPENDENCY_VULNERABILITIES_CHART_KEYS = [
  'DEPENDENCY_VULNERABILITIES',
];

export const CUSTOM_CHART_KEYS = ['CUSTOM'];

export const ISSUE_AGE_CHART_KEYS = [
  'CHANGE_AGING_DISTRIBUTION',
  'ISSUE_CYCLE_TIME_DIST',
];

export const REQUIREMENT_STABILITY_CHARTS = ['REQUIREMENT_STABILITY'];

export const PR_AGEING_CHARTS = ['PR_AGEING_DISTRIBUTION'];

export const REVIEW_RESPONSIVENESS_INDEX_CHARTS = [
  'REVIEW_RESPONSIVENESS_INDEX',
];

export const PR_HEALTH_INDEX_CHARTS = ['PR_HEALTH_INDEXES'];

export const LOC_VS_PR_CYCLE_TIME_CHARTS = ['LOC_VS_PR_CYCLE_TIME'];

export const ACTIVE_DAYS_CHARTS = ['ACTIVE_DAYS'];

export const WORK_BREAKDOWN_CHARTS = [
  'WORK_BREAKDOWN',
  'WORK_BREAKDOWN_CODE_IMPACT',
];

export const WORK_BREAKDOWN_STATS_CHARTS = [
  'WORK_BREAKDOWN_NEW_WORK',
  'WORK_BREAKDOWN_REFACTOR',
  'WORK_BREAKDOWN_HELP_OTHERS',
  'WORK_BREAKDOWN_RE_WORK',
];

export const ISSUE_LIFECYCLE_DIST_CHARTS = [
  'ISSUE_LIFECYCLE_DIST',
  'ISSUE_PROGRESSION_RATE',
  'BACKLOG_CYCLE_TIME',
];

export const MEETING_CHARTS = ['MEETING_COUNT', 'MEETING_TIME'];

export const ISSUE_REPORT_DETAIL_FOR_RECRUIT_CRM_CHARTS = [
  'OUTCOME_POINTS',
  'ACHIEVED_OUTCOME_POINTS',
];

export const DOCUMENT_UPDATED_CHARTS = ['DOCUMENT_UPDATED'];

export const SPRINT_CHARTS = [
  'SPRINT_COMPLETION_PERCENT',
  'SPRINT_ADDITION_RATE',
  'SPRINTS_COMPLETED',
  'SPRINT_VELOCITY',
];

export const TEST_RUN_CHARTS = [
  'TEST_SUCCESS_RATE',
  'TEST_RUN_COUNT',
  'TEST_RUN_TIME',
];

export const TEST_DETAIL_CHARTS = ['TEST_COUNT'];

export const DATA_POINT_KEYS = [
  'ACTIVE_WORKLOAD',
  'TOTAL_WORKLOAD',
  'OVERDUE_TICKETS',
  'BLOCKED_TICKETS',
  'GIT_ACTIVITY',
  'AI_ADOPTION',
  'ISSUE_ACTIVITY',
  'DORA_ACTIVITY',
];

export const AI_IMPACT_CHART_KEYS = [
  'PR_MERGE',
  'PR_CHANGES',
  'BUG_CLOSE',
  'TASK_CLOSE',
  'CONTRIBUTOR_COUNT',
  'COMMENTS_PER_PR',
  'PR_CYCLE_TIME',
];

export const countCategory = (metrics: any, category: any) => {
  return metrics.reduce((acc: number, tag: any) => {
    return acc + (tag?.tabs?.includes(category) ? 1 : 0);
  }, 0);
};

export const truncateText = (text: string, maxLength = 15) =>
  text?.length > maxLength ? `${text?.slice(0, maxLength)}...` : text;

export const ISSUE_REPORT_DETAIL_FOR_RECRUIT_CRM_PRIORITY_DISTRIBUTED_DATA_CHARTS =
  [
    'SUB_PAR_REVIEWER_DATA_CRITICAL',
    'SUB_PAR_REVIEWER_DATA_OTHER',
    'BUG_CAUSED_BY_DATA_CRITICAL',
    'BUG_CAUSED_BY_DATA_OTHER',
  ];

export const graphColorArr = [
  '#0B698B',
  '#C2528B',
  '#FF8D70',
  '#5F50A9',
  '#0396A6',
  '#F1B813',
  '#490B3E',
];

export type AuthorIdFilter = {
  author: string;
  type?: string;
  status?: string;
  age?: string;
  name?: string;
  sprintId?: string;
  projectsFilter?: string[];
  lowerBoundIncl?: any;
  upperBound?: any;
  sdlcStageId?: string;
  controlChartFilters?: {
    includeBelowP5?: boolean | null;
    includeP5ToP10?: boolean | null;
    includeP10ToP90?: boolean | null;
    includeP90ToP95?: boolean | null;
    includeAboveP95?: boolean | null;
    p5?: number | null;
    p10?: number | null;
    p90?: number | null;
    p95?: number | null;
  };
  showControlChart?: any;
  prViewFilters?: any;
  view?: string;
};

export type TeamFilter = {
  author?: string;
  teamId: string;
  type?: string;
  status?: string;
  view?: string;
  age?: string;
  name?: string;
  sprintId?: string;
  projectsFilter?: string[];
  lowerBoundIncl?: any;
  upperBound?: any;
  sdlcStageId?: string;
  controlChartFilters?: {
    includeBelowP5?: boolean | null;
    includeP5ToP10?: boolean | null;
    includeP10ToP90?: boolean | null;
    includeP90ToP95?: boolean | null;
    includeAboveP95?: boolean | null;
    p5?: number | null;
    p10?: number | null;
    p90?: number | null;
    p95?: number | null;
  };
  showControlChart?: any;
  prViewFilters?: any;
};

export const statusLabels = {
  ideation: 'Ideation',
  prd: 'Product Requirement Document',
  uxDesign: 'UX Design',
  techDesign: 'Tech Design',
  development: 'Development',
  qa: 'Quality Assurance',
  blocked: 'Blocked',
  deployment: 'Deployment',
} as any;

export function getKeyByValue(
  value: string
): keyof typeof statusLabels | undefined {
  return (Object.keys(statusLabels) as Array<keyof typeof statusLabels>).find(
    (key) => statusLabels[key] === value
  );
}

export function getMapFromArray(
  array: { [key: string]: string | null }[],
  key: string,
  value: string
): Record<string, string | null> {
  return array.reduce((acc, item) => {
    if (item && item[key]) {
      acc[item[key] as string] = item[value];
    }
    return acc;
  }, {});
}

export function extractPercentageAndRemaining(message: string) {
  const regex = /([+-]?\d+(\.\d+)?)%/;
  const match = message.match(regex);
  let percentage = '';
  let remaining = '';

  if (match) {
    percentage = match[0];
    remaining = message.replace(percentage, '').trim();
  } else {
    remaining = message;
  }

  return { percentage, remaining };
}

export const formatMessageWithColoredPercentage = (
  message: string,
  color: string
): ReactNode => {
  const percentageRegex = /(-?\d+%(?:\(\d+\))?)/;
  const parts = message?.split(percentageRegex);

  if (parts.length <= 1) return message;

  return (
    <>
      {parts?.map((part, index) => {
        if (percentageRegex.test(part)) {
          return (
            <Text key={index} as="span" fontWeight="semibold" color={color}>
              {part}
            </Text>
          );
        }
        return (
          <Text key={index} as="span">
            {part}
          </Text>
        );
      })}
    </>
  );
};

export const getOrdinalSuffix = (num: number) => {
  if (!num) return '';
  const remainderTen = num % 10;
  const remainderHundred = num % 100;

  if (remainderTen === 1 && remainderHundred !== 11) {
    return 'st';
  } else if (remainderTen === 2 && remainderHundred !== 12) {
    return 'nd';
  } else if (remainderTen === 3 && remainderHundred !== 13) {
    return 'rd';
  } else {
    return 'th';
  }
};

export function singularizeWord(number: number, word: string) {
  if (number === 1.0) {
    return word?.replace(/s$/, '');
  }
  return word;
}

/**
 * Returns a YYYY-MM-DD string based on the **local** timezone.
 */
export const getLocalDateStr = (date?: Date): string => {
  const d = date ?? new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDateToReadableString = (dateString: string): string => {
  const date = new Date(dateString);

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const fillColors = [
  '#C2528B',
  '#5F50A9',
  '#0D7EA0',
  '#EC4899',
  '#49CC93',
  '#BBBBBC',
  '#FFC532',
  '#80E1D9',
];

export const fillColorsTaskDist = [
  '#0095E6',
  '#49CC93',
  '#EC4899',
  '#5F50A9',
  '#FFC532',
  '#FF7C65',
  '#C2528B',
  '#FF9D52',
  '#0D7EA0',
  '#80E1D9',
  '#BBBBBC',
];

export const formatGoalStatus = (value: string): string => {
  if (!value) return '';

  const normalizedValue = value?.toString()?.trim();

  const words = normalizedValue?.split(/[_\s]+/);

  return words
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    ?.join(' ');
};

export const getPercentage = (value: number, total: number) => {
  return parseFloat(((value / total) * 100).toFixed(2));
};

export const progressBarColors = {
  atRisk: '#E65100',
  behind: '#FFAD00',
  onTrack: '#1DA44A',
};

export type SegmentProps = {
  label: string;
  value: number;
  color: string;
  percentage: number;
  isFirst?: boolean;
  isLast?: boolean;
};

export type LegendItemProps = {
  label: string;
  value: number;
  total: number;
  color: string;
  hasBorder?: boolean;
};

export const formatTimeUnit = (
  value: number,
  unit:
    | 'Days'
    | 'Minutes'
    | 'Count'
    | 'Percentage'
    | 'INTEGER'
    | 'TIME'
    | 'PERCENTAGE'
    | 'New Lines Added'
    | 'Code Assistance Lines Accepted'
    | 'Average',
  metric: string
) => {
  if (unit === 'Days') {
    return metric === 'ACTIVE_DAYS' ? `${value} Days/Week` : `${value} Days`;
  }

  if (unit === 'Minutes' || unit === 'TIME') {
    return splitTime(value / 60);
  }

  if (unit === 'Count' || unit === 'INTEGER' || unit === 'Average') {
    return value;
  }
  if (unit === 'Percentage' || unit === 'PERCENTAGE') {
    return `${value}%`;
  }
  if (unit === 'New Lines Added' || unit === 'Code Assistance Lines Accepted') {
    if (value == null || isNaN(value)) {
      return '0';
    }
    return `${locFormat(value)} ${unit}`;
  }

  // For Count type
  return `${value} ${unit || ''}`;
};

export const formatDateForDisplay = (dateString: string | null) => {
  if (!dateString) return '';
  return moment(dateString).format('D MMM');
};

export const getContributorActivityFilterOptions = (
  startDate?: string | null,
  endDate?: string | null,
  includeExcluded = true
) => {
  const options = [
    { value: 'all', label: 'All' },
    {
      value: 'active',
      label:
        startDate && endDate
          ? `Last Activity in (${formatDateForDisplay(
              startDate
            )} - ${formatDateForDisplay(endDate)})`
          : 'Last Activity in',
    },
    {
      value: 'inactive',
      label:
        startDate && endDate
          ? `No Activity in (${formatDateForDisplay(
              startDate
            )} - ${formatDateForDisplay(endDate)})`
          : 'Inactive',
    },
  ];

  if (includeExcluded) {
    options.push({ value: 'excluded', label: 'Excluded' });
  }

  return options;
};

export const getContributorActivityFilterLabel = (
  value: any,
  startDate?: string | null,
  endDate?: string | null
) => {
  switch (value) {
    case '':
      return 'All';
    case 'active':
      if (startDate && endDate) {
        return `Last Activity in (${formatDateForDisplay(
          startDate
        )} - ${formatDateForDisplay(endDate)})`;
      }
      return 'Active';
    case 'inactive':
      if (startDate && endDate) {
        return `No Activity in (${formatDateForDisplay(
          startDate
        )} - ${formatDateForDisplay(endDate)})`;
      }
      return 'Inactive';
    case 'excluded':
      return 'Excluded Accounts';
    case 'official':
      return 'Merged Accounts';
    case 'unofficial':
      return 'Unmerged Accounts';
    case 'mergeSuggestion':
      return 'Merge Suggestions';
    case 'versionControl':
      return 'Version Control Accounts';
    case 'issueControl':
      return 'Issue Management Accounts';
    default:
      return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }
};

export const getActivityFilters = (activityFilter: string) => {
  switch (activityFilter) {
    case 'active':
      return {
        activeFilters: { field: 'status', value: 'all' },
        excluded: false,
        inActiveFilters: null,
      };
    case 'excluded':
      return {
        activeFilters: null,
        excluded: true,
        inActiveFilters: null,
      };
    case 'inactive':
      return {
        activeFilters: null,
        excluded: false,
        inActiveFilters: { field: 'status', value: 'all' },
      };
    default:
      return {
        activeFilters: null,
        excluded: false,
        inActiveFilters: null,
      };
  }
};
